// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 26-versioning-update-strategy.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { Disclosure } from '@/components/greenlight/Disclosure';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { api } from '@/lib/api';
import { HEALTH_POLL_MS, POSTURE_KEY } from '@/lib/healthPoll';
import type { HealthSnapshot } from '@greenlight/shared';

import { UpdaterPostureDetail, UpdaterPostureSummary } from './UpdaterPosture';
import { UpdaterRunActions } from './UpdaterRunActions';
import { UpdaterRunsList } from './UpdaterRunsList';
import { phaseLabel } from '@/components/health/updaterRunMeta';

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-2 py-2 border-t border-border first:border-t-0 first:pt-0 text-sm">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className="min-w-0 font-medium text-foreground text-right">{children}</dd>
    </div>
  );
}

/**
 * The Internal Health Updater panel, in the order an operator asks the
 * questions: one line of posture, the two org-admin run actions, a current
 * failure if there is one, then the retained run history with a detail per run.
 *
 * Everything else the card knows — the schedule, the prerelease posture, which
 * of installer/updater applied the running release, the updater image, the last
 * check and apply — is a click away rather than on the first screen. Those
 * answer questions an operator asks about once a year; the first screen belongs
 * to the two asked every time, "am I current" and "did the last run work".
 *
 * A failure the install is still sitting on keeps its bounded reason code and
 * the packaged-installer command at the top level — the remediation is not
 * detail to fold away. One a later apply has already superseded is not shown
 * here at all; it is history, and the run history holds it.
 */
export function UpdaterSection({
  snapshot,
}: {
  readonly snapshot: HealthSnapshot;
}): React.ReactElement {
  const updater = snapshot.updater;
  const updaterImage = snapshot.versions.updater_image;
  const postureQuery = api.health.updaterPosture.useQuery(POSTURE_KEY, undefined, {
    queryKey: POSTURE_KEY,
    refetchInterval: HEALTH_POLL_MS,
  });
  const posture = postureQuery.data?.status === 200 ? postureQuery.data.body : null;
  // Enablement has two readings and the actions must satisfy both. The chart
  // injects the updater image only where the updater is enabled, so its absence
  // is how the snapshot says there is nothing to run (docs/26 §Migration
  // contract); the live CronJob read is what the headline states. While they
  // disagree — a collector tick behind a chart rerun, a deleted CronJob — the
  // actions stay hidden, so the card never offers a run directly beneath a line
  // saying this install does not update itself. `!== false` rather than
  // `=== true` keeps them through a slow or failed posture read.
  const updaterEnabled =
    updaterImage !== 'unknown' && updaterImage.length > 0 && posture?.updates_enabled !== false;

  const recordedFailure = updater.last_failure ?? null;
  const activePhase = updater.active_phase ?? null;
  const hasActivity =
    new Date(updater.last_check_at).getTime() > 0 ||
    updater.last_apply_at !== null ||
    recordedFailure !== null ||
    activePhase !== null;

  // `last_failure` is the newest failed row the store holds, with no time
  // bound: unqualified it would report a resolved problem as this install's
  // state for as long as retention keeps the run. Only a failure no apply has
  // succeeded past is current.
  const currentFailure =
    recordedFailure !== null &&
    (updater.last_apply_at === null ||
      new Date(recordedFailure.at).getTime() > new Date(updater.last_apply_at).getTime())
      ? recordedFailure
      : null;

  return (
    <SectionCard
      title="Updater"
      // Only live state. Currency is the headline's answer, and `up_to_date`
      // comes from a different, staler read — badging it here can contradict
      // the line directly beneath it.
      badge={
        activePhase !== null ? (
          <StatusBadge variant="deploying">{phaseLabel(activePhase)}</StatusBadge>
        ) : undefined
      }
      className="md:col-span-2"
    >
      <div className="space-y-4">
        <UpdaterPostureSummary
          posture={posture}
          isLoading={postureQuery.isLoading}
          isError={postureQuery.isError}
        />

        {updaterEnabled ? <UpdaterRunActions runInFlight={activePhase !== null} /> : null}

        {currentFailure !== null && (
          <div
            className="rounded-gl border border-[hsl(var(--status-red)/0.3)] bg-[hsl(var(--status-red)/0.05)] p-3"
            data-testid="updater-failure"
          >
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge variant="failed">{currentFailure.reason}</StatusBadge>
              <span className="text-sm text-muted-foreground">
                {phaseLabel(currentFailure.phase)} · {currentFailure.release_version} ·{' '}
                {relativeTime(currentFailure.at)}
              </span>
            </div>
            <p className="mt-2 text-sm text-foreground">
              {`The last run stopped at ${phaseLabel(currentFailure.phase)} and left the installation unchanged.`}
            </p>
            {currentFailure.installer_command !== null && (
              <div className="mt-2">
                <p className="text-[11px] text-muted-foreground mb-1">
                  To apply this release manually, run the packaged installer:
                </p>
                <MonoValue className="block break-all">
                  {currentFailure.installer_command}
                </MonoValue>
              </div>
            )}
          </div>
        )}

        <Disclosure
          label="Update settings and last run"
          hint="schedule, prereleases, image"
          testId="updater-detail"
        >
          <div className="space-y-3">
            {posture !== null ? (
              <UpdaterPostureDetail posture={posture} />
            ) : (
              <p className="text-sm text-muted-foreground">
                {postureQuery.isLoading
                  ? 'Reading the update settings…'
                  : 'Couldn’t read the update settings. Retry in a moment.'}
              </p>
            )}
            {hasActivity ? (
              <dl className="border-t border-border pt-1 text-sm">
                {updaterImage !== 'unknown' && (
                  <Row label="Updater image">
                    <MonoValue className="truncate max-w-[280px]">{updaterImage}</MonoValue>
                  </Row>
                )}
                <Row label="Last check">
                  {new Date(updater.last_check_at).getTime() > 0 ? (
                    <span>
                      {relativeTime(updater.last_check_at)}{' '}
                      <span className="text-muted-foreground">
                        · {phaseLabel(updater.last_check_result)}
                      </span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">never</span>
                  )}
                </Row>
                <Row label="Last successful apply">
                  {updater.last_apply_at !== null ? (
                    <span>
                      {relativeTime(updater.last_apply_at)}
                      {updater.last_apply_version ? (
                        <span className="text-muted-foreground">
                          {' '}
                          · <MonoValue>{updater.last_apply_version}</MonoValue>
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">none yet</span>
                  )}
                </Row>
              </dl>
            ) : (
              <p className="border-t border-border pt-3 text-sm text-muted-foreground">
                No updater run has recorded anything yet.
              </p>
            )}
          </div>
        </Disclosure>

        <UpdaterRunsList />
      </div>
    </SectionCard>
  );
}
